/**
 * Board — Cytoscape graph management.
 * Handles node/edge creation, layout, selection, and Steiner tree integration.
 */

import cytoscape from 'cytoscape';
import { CY_STYLE, cyLayout, isActorNode, isMediaNode } from './constants.js';
import { findSteinerTree } from './steiner.js';
import { imgUrl } from './tmdb.js';

export class Board {
  constructor(containerEl, targetNodeIds = new Set()) {
    this.container = containerEl;
    this.targetNodeIds = targetNodeIds;
    this.cy = null;
    this.hintNodes = new Map(); // nodeId -> { source }
    this._layoutInstance = null;
  }

  init() {
    if (this.cy) this.cy.destroy();

    this.cy = cytoscape({
      container: this.container,
      style: CY_STYLE,
      elements: [],
      userZoomingEnabled: true,
      userPanningEnabled: true,
      boxSelectionEnabled: false,
      selectionType: 'single',
      minZoom: 0.3,
      maxZoom: 3,
    });

    // Try d3-force layout
    this._startLayout();
    return this;
  }

  _startLayout() {
    try {
      const layoutOpts = cyLayout(this.container);
      this._layoutInstance = this.cy.layout(layoutOpts);
      this._layoutInstance.run();
    } catch {
      // Fallback to cose if d3-force not available
      this._layoutInstance = this.cy.layout({
        name: 'cose', animate: true, fit: true,
        nodeRepulsion: 8000, padding: 20,
      });
      this._layoutInstance.run();
    }
  }

  destroy() {
    if (this._layoutInstance) {
      try { this._layoutInstance.stop(); } catch {}
    }
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
  }

  /** Add a node to the board */
  addNode(nodeId, data) {
    if (!this.cy || this.cy.getElementById(nodeId).length) return;

    const isTarget = this.targetNodeIds.has(nodeId);
    const isActor = isActorNode(nodeId);

    this.cy.add({
      group: 'nodes',
      data: {
        id: nodeId,
        label: data.label || '',
        image: data.image || '',
        shape: isActor ? 'ellipse' : 'round-rectangle',
        backgroundColor: isTarget ? '#1a1510' : (isActor ? '#0d0d12' : '#0d0d12'),
        borderColor: isTarget ? '#BCA356' : (isActor ? 'rgba(255,255,255,0.15)' : 'rgba(188,163,86,0.35)'),
        borderWidth: isTarget ? 3 : 1,
        opacity: 1,
        type: data.type || (isActor ? 'actor' : 'media'),
        genres: data.genres || [],
        year: data.year || '',
      },
    });

    // Animate appearance
    const node = this.cy.getElementById(nodeId);
    if (node.length) {
      node.style('opacity', 0);
      node.animate({ style: { opacity: 1 } }, { duration: 200 });
    }
  }

  /** Add an edge between two nodes */
  addEdge(sourceId, targetId) {
    if (!this.cy) return;
    const edgeId = sourceId + '-' + targetId;
    const reverseId = targetId + '-' + sourceId;
    if (this.cy.getElementById(edgeId).length || this.cy.getElementById(reverseId).length) return;

    this.cy.add({
      group: 'edges',
      data: {
        id: edgeId,
        source: sourceId,
        target: targetId,
        lineColor: '#fff',
        width: 1,
      },
    });
  }

  /** Add a node with its connections to existing nodes */
  addNodeWithConnections(nodeId, nodeData, connectedIds) {
    this.addNode(nodeId, nodeData);
    for (const connId of connectedIds) {
      if (this.cy.getElementById(connId).length) {
        this.addEdge(nodeId, connId);
      }
    }
  }

  /** Mark a node as a hint */
  markHint(nodeId, sourceId) {
    this.hintNodes.set(nodeId, { source: sourceId });
    // Color the hint edge purple
    const edgeIds = [nodeId + '-' + sourceId, sourceId + '-' + nodeId];
    for (const eid of edgeIds) {
      const el = this.cy.getElementById(eid);
      if (el.length) el.data('lineColor', '#5E51AE');
    }
  }

  /** Get adjacency map from the current Cytoscape graph */
  getAdjacency() {
    if (!this.cy) return new Map();
    const adj = new Map();
    this.cy.nodes().forEach(node => {
      adj.set(node.data().id, new Set());
    });
    this.cy.edges().forEach(edge => {
      const s = edge.data().source, t = edge.data().target;
      if (adj.has(s) && adj.has(t)) {
        adj.get(s).add(t);
        adj.get(t).add(s);
      }
    });
    return adj;
  }

  /** Run the Steiner tree on the current board */
  findBestPath(excludeNodeIds) {
    const adj = this.getAdjacency();
    return findSteinerTree(adj, this.targetNodeIds, excludeNodeIds);
  }

  /** Highlight the best path edges in gold */
  highlightBestPath(result) {
    if (!this.cy || !result) return;

    // Reset all edges
    this.cy.$('edge').data('width', 1).data('lineColor', '#fff');

    // Highlight optimal tree edges
    for (const [uid, vid] of (result.treeEdgeIds || [])) {
      for (const eid of [uid + '-' + vid, vid + '-' + uid]) {
        const el = this.cy.getElementById(eid);
        if (el.length) {
          el.data('lineColor', '#BCA356').data('width', 5);
          break;
        }
      }
    }

    // Restore hint edge colors on top
    for (const [nid, info] of this.hintNodes) {
      for (const eid of [nid + '-' + info.source, info.source + '-' + nid]) {
        const el = this.cy.getElementById(eid);
        if (el.length) el.data('lineColor', '#5E51AE');
      }
    }
  }

  /** Get all node IDs on the board */
  getNodeIds() {
    if (!this.cy) return [];
    return this.cy.nodes().map(n => n.data().id);
  }

  /** Get node data by ID */
  getNodeData(nodeId) {
    if (!this.cy) return null;
    const el = this.cy.getElementById(nodeId);
    if (!el.length) return null;
    return el.data();
  }

  /** Check if a node exists on the board */
  hasNode(nodeId) {
    return this.cy && this.cy.getElementById(nodeId).length > 0;
  }

  /** Get node counts */
  getCounts() {
    if (!this.cy) return { actors: 0, titles: 0 };
    let actors = 0, titles = 0;
    this.cy.nodes().forEach(n => {
      const id = n.data().id;
      if (isActorNode(id)) actors++;
      else if (isMediaNode(id)) titles++;
    });
    return { actors, titles };
  }

  /** Center and fit the graph */
  fit() {
    if (this.cy) this.cy.fit(undefined, 30);
  }

  /** Search nodes by label */
  searchNodes(query) {
    if (!this.cy || !query) return [];
    const q = query.toLowerCase();
    const results = [];
    this.cy.nodes().forEach(n => {
      const label = n.data().label || '';
      if (label.toLowerCase().includes(q)) {
        results.push({ id: n.data().id, label });
      }
    });
    return results;
  }

  /** Highlight matching nodes, dim others */
  highlightSearch(matchIds) {
    if (!this.cy) return;
    if (!matchIds || matchIds.length === 0) {
      this.cy.nodes().style('opacity', 1);
      return;
    }
    const matchSet = new Set(matchIds);
    this.cy.nodes().forEach(n => {
      n.style('opacity', matchSet.has(n.data().id) ? 1 : 0.2);
    });
  }

  /** Export board state for serialization */
  exportState() {
    if (!this.cy) return { nodes: [], edges: [] };
    const nodes = [];
    this.cy.nodes().forEach(n => nodes.push(n.data()));
    const edges = [];
    this.cy.edges().forEach(e => edges.push(e.data()));
    return { nodes, edges };
  }
}
